import React, { useState } from 'react';
import { CheckCircle2, Loader2, Pencil, RotateCcw, Send, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { fmtWhen } from './flagUtils';

const show = (v) => (Array.isArray(v) ? v.join(', ') : v) || '(blank)';

const NoteEditor = ({ note, canEdit, onSave }) => {
  const [text, setText] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); const ok = await onSave(note.id, text); setSaving(false); if (ok) setText(null); };
  return (
    <div className="space-y-3 rounded-lg border p-3" data-testid={`ticket-source-note-${note.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div><div className="text-sm font-medium">{note.subject}</div><div className="text-xs text-muted-foreground">Case note used for the {note.summary_label}{note.date_of_service ? ` · ${note.date_of_service}` : ''}</div></div>
        {canEdit && text === null && <Button size="sm" variant="outline" onClick={() => setText(note.content || '')} data-testid="ticket-edit-note-btn"><Pencil className="h-4 w-4 mr-2" />Edit Case Note</Button>}
      </div>
      {text === null ? <p className="text-sm whitespace-pre-wrap">{note.content}</p> : (
        <div className="space-y-2">
          <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} data-testid="ticket-note-textarea" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setText(null)} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving || !text.trim()} data-testid="ticket-save-note-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Keep Change</Button>
          </div>
        </div>
      )}
      <div className="rounded-md bg-muted/50 p-2"><div className="text-xs font-medium flex items-center gap-1"><Sparkles className="h-3 w-3" />{note.summary_label}</div>
        <p className="text-sm whitespace-pre-wrap" data-testid={`ticket-summary-${note.kind}`}>{note.summary || '-'}</p>
        {note.drafted && <p className="text-xs text-muted-foreground mt-1" data-testid={`ticket-note-drafted-${note.id}`}>Edited on this ticket. The summary will be regenerated when corrections are submitted.</p>}</div>
    </div>
  );
};

export const SourceNotesCard = ({ notes, canEdit, onSave }) => (notes.length ? (
  <Card className="card-shadow"><CardHeader><CardTitle className="text-lg">Case Note & AI Summary</CardTitle>
    <CardDescription>Case note changes are kept on this ticket. When corrections are submitted, the case note is updated and the AI summary is regenerated on the Caseload Review.</CardDescription></CardHeader>
    <CardContent className="space-y-3">{notes.map((n) => <NoteEditor key={n.id + n.kind} note={n} canEdit={canEdit} onSave={onSave} />)}</CardContent></Card>
) : null);

export const ComparisonCard = ({ rows }) => (
  <Card className="card-shadow" data-testid="ticket-comparison"><CardHeader><CardTitle className="text-lg">What Changed</CardTitle><CardDescription>Before and after for every correction on this ticket.</CardDescription></CardHeader>
    <CardContent className="p-0">
      <Table><TableHeader><TableRow><TableHead>Field</TableHead><TableHead>Before</TableHead><TableHead>After</TableHead><TableHead>Changed</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No changes yet</TableCell></TableRow>}
          {rows.map((r, i) => (
            <TableRow key={`${r.field}-${i}`} data-testid="ticket-comparison-row">
              <TableCell className="font-medium text-sm">{r.label}</TableCell>
              <TableCell className="text-sm text-muted-foreground max-w-xs whitespace-pre-wrap">{show(r.before)}</TableCell>
              <TableCell className="text-sm max-w-xs whitespace-pre-wrap">{show(r.after)}</TableCell>
              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{r.changed_by}<br />{fmtWhen(r.changed_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
    </CardContent></Card>
);

export const FollowUpCard = ({ flag, canAct, busy, onGenerate, onSubmit }) => {
  const [note, setNote] = useState('');
  const fs = flag.follow_up_summary;
  const current = fs && fs.round === (flag.round || 1);
  return (
    <Card className="card-shadow" data-testid="ticket-follow-up"><CardHeader><CardTitle className="text-lg flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />AI Follow-up Summary</CardTitle>
      <CardDescription>{canAct ? 'When your corrections are finished, generate the summary, read it, add comments if needed, then submit for management approval.' : 'Summary of the actions the Case Manager took.'}</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        {fs ? <div className="rounded-lg border p-3"><p className="text-sm whitespace-pre-wrap" data-testid="ticket-follow-up-text">{fs.text}</p>
          <p className="text-xs text-muted-foreground mt-2">Generated by {fs.generated_by_name} · {fmtWhen(fs.generated_at)}{!current ? ' · from a previous round' : ''}</p></div>
          : <p className="text-sm text-muted-foreground">Not generated yet.</p>}
        {flag.submit_comment && !canAct && <p className="text-sm"><span className="font-medium">Case Manager comments:</span> {flag.submit_comment}</p>}
        {canAct && (
          <div className="space-y-2">
            <Button variant="outline" onClick={onGenerate} disabled={busy} data-testid="ticket-generate-follow-up-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}{fs ? 'Regenerate Follow-up Summary' : 'Generate Follow-up Summary'}</Button>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Comments for management (optional)" data-testid="ticket-submit-comment" />
            <div className="flex justify-end"><Button disabled={busy || !current} onClick={() => onSubmit(note)} data-testid="flag-submit-corrections-btn"><Send className="h-4 w-4 mr-2" />Submit Corrections</Button></div>
          </div>
        )}
      </CardContent></Card>
  );
};

export const ManagementReviewCard = ({ busy, onApprove, onReturn }) => {
  const [comment, setComment] = useState('');
  return (
    <Card className="card-shadow border-blue-200 dark:border-blue-800" data-testid="ticket-management-review"><CardHeader><CardTitle className="text-lg">Management Review</CardTitle>
      <CardDescription>Compare the changes against your original concerns, then approve or return the ticket.</CardDescription></CardHeader>
      <CardContent className="space-y-2">
        <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comments (required to return for correction)" data-testid="ticket-review-comment" />
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" disabled={busy || !comment.trim()} onClick={() => onReturn(comment)} className="border-orange-400 text-orange-700 hover:bg-orange-50" data-testid="ticket-return-btn"><RotateCcw className="h-4 w-4 mr-2" />Return for Correction</Button>
          <Button disabled={busy} onClick={() => onApprove(comment)} className="bg-green-600 hover:bg-green-700" data-testid="ticket-approve-btn"><CheckCircle2 className="h-4 w-4 mr-2" />Approve & Resolve</Button>
        </div>
      </CardContent></Card>
  );
};
